import React, {useState} from 'react';
import {Store} from '../../models/store.model';
import {AppText, AppBodyText, AppLabelText, AppSmallText} from '../../../../Presentation/components/ui/Typography';
import {FileText, Image, Eye, ExternalLink, Copy, Check} from 'lucide-react';
import {useNotification} from '../../../../Services/useNotification';

interface StoreDocumentsInfoProps {
  store: Store;
}

export const StoreDocumentsInfo: React.FC<StoreDocumentsInfoProps> = ({store}) => {
  const {success: showSuccess, error: showError} = useNotification();

  const [showImageModal, setShowImageModal] = useState<{
    isOpen: boolean;
    imageUrl: string;
    title: string;
  }>({
    isOpen: false,
    imageUrl: '',
    title: ''
  });

  const [copiedItems, setCopiedItems] = useState<{
    commercialRegister: boolean;
    taxCard: boolean;
  }>({
    commercialRegister: false,
    taxCard: false
  });

  const handleImageClick = (imageUrl: string, title: string) => {
    setShowImageModal({
      isOpen: true,
      imageUrl,
      title
    });
  };

  const closeImageModal = () => {
    setShowImageModal({
      isOpen: false,
      imageUrl: '',
      title: ''
    });
  };

  const copyToClipboard = async (text: string, type: 'commercialRegister' | 'taxCard') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => ({
        ...prev,
        [type]: true
      }));

      // Show success notification
      const fieldName = type === 'commercialRegister' ? 'رقم السجل التجاري' : 'رقم البطاقة الضريبية';
      showSuccess(`تم نسخ ${fieldName} بنجاح`);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedItems(prev => ({
          ...prev,
          [type]: false
        }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text: ', error);
      const fieldName = type === 'commercialRegister' ? 'رقم السجل التجاري' : 'رقم البطاقة الضريبية';
      showError(`فشل في نسخ ${fieldName}`);
    }
  };

  // Don't render if no documents are available
  if (!store.commercialRegisterNumber && !store.commercialRegisterImage &&
      !store.taxCardNumber && !store.taxCardImage) {
    return null;
  }

  return (
    <>
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">الوثائق والمستندات</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Commercial Register */}
          {(store.commercialRegisterNumber || store.commercialRegisterImage) && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-brand-200">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-5 h-5 text-brand-600" />
                <AppLabelText className="text-brand-700 font-medium">السجل التجاري</AppLabelText>
              </div>

              {store.commercialRegisterNumber && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <AppSmallText className="text-brand-600">رقم السجل</AppSmallText>
                    <button
                      onClick={() => copyToClipboard(store.commercialRegisterNumber!, 'commercialRegister')}
                      className="flex items-center gap-1 px-2 py-1 bg-brand-600 text-white rounded text-xs hover:bg-brand-700 transition-colors"
                      title="نسخ رقم السجل التجاري"
                    >
                      {copiedItems.commercialRegister ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>تم النسخ</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>نسخ</span>
                        </>
                      )}
                    </button>
                  </div>
                  <AppBodyText className="font-bold text-brand-900" dir="ltr">
                    {store.commercialRegisterNumber}
                  </AppBodyText>
                </div>
              )}

              {store.commercialRegisterImage && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleImageClick(store.commercialRegisterImage!, 'السجل التجاري')}
                    className="flex items-center gap-2 px-3 py-1 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    عرض الصورة
                  </button>
                  <a
                    href={store.commercialRegisterImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    فتح في تبويب جديد
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Tax Card */}
          {(store.taxCardNumber || store.taxCardImage) && (
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="w-5 h-5 text-green-600" />
                <AppLabelText className="text-green-700 font-medium">البطاقة الضريبية</AppLabelText>
              </div>

              {store.taxCardNumber && (
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <AppSmallText className="text-green-600">رقم البطاقة</AppSmallText>
                    <button
                      onClick={() => copyToClipboard(store.taxCardNumber!, 'taxCard')}
                      className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                      title="نسخ رقم البطاقة الضريبية"
                    >
                      {copiedItems.taxCard ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>تم النسخ</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>نسخ</span>
                        </>
                      )}
                    </button>
                  </div>
                  <AppBodyText className="font-bold text-green-900" dir="ltr">
                    {store.taxCardNumber}
                  </AppBodyText>
                </div>
              )}

              {store.taxCardImage && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleImageClick(store.taxCardImage!, 'البطاقة الضريبية')}
                    className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    <Eye className="w-4 h-4" />
                    عرض الصورة
                  </button>
                  <a
                    href={store.taxCardImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    فتح في تبويب جديد
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{showImageModal.title}</h3>
              <button
                onClick={closeImageModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4">
              <img
                src={showImageModal.imageUrl}
                alt={showImageModal.title}
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
